import React from "react";
import gql from "graphql-tag";
import { Link, navigate, RouteComponentProps } from "@reach/router";
import times from "lodash/times";
import { BrandCircle } from "@organisms/brandCircleComponents";
import { FavoriteBrands_Viewer_favoriteBrands as TBrand } from "@queries";
import { Box, Flex } from "@atoms";
import { constants, RailsUrl } from "@services";
import { ContentBlock, CircleAddIcon, CircleText } from "@molecules";

// == Types ================================================================

interface IProps {
  brands: TBrand[];
}

// == Constants ============================================================

const FRAGMENTS = {
  fields: gql`
    fragment BrandFavoritesDialerFragment on Brand {
      id
      name
      logoImage
      logoBackgroundColor
      invertedLogoImage
      viewerHasFavorited
    }
  `,
};

const MAX_NUM_CIRCLES = 7;
const PER_ROW = Math.ceil(MAX_NUM_CIRCLES / 2);

// == Component ============================================================

const BrandFavoritesDialer = ({ brands }: IProps) => {
  const brandsCount = brands.length;
  const brandOverflowCount = brandsCount - MAX_NUM_CIRCLES;

  const onEditClick = () => {
    navigate(RailsUrl.favoritesUrl());
  };

  const renderBrandCircle = (brand) => {
    return (
      <Link to={RailsUrl.brandModalUrl({ id: brand.id })}>
        <BrandCircle brand={brand} />
      </Link>
    );
  };

  return (
    <ContentBlock title="Favorites" altLink="Edit" altOnClick={onEditClick}>
      <Flex flexWrap="wrap" mx={-1}>
        {times(8, (index) => {
          // No brand in this position
          if (!brands[index]) {
            return (
              <Box
                key={`brand-fab-empty-circle-${index}`}
                mt={index < PER_ROW ? 0 : 2}
                px={2}
                width={1 / PER_ROW}
              >
                <Link to={`?modal=${constants.favoritesParam}`}>
                  <CircleAddIcon />
                </Link>
              </Box>
            );
          }

          // Last item, and greater than brands to show
          if (index === MAX_NUM_CIRCLES && brandsCount > MAX_NUM_CIRCLES) {
            return (
              <Box
                key={`brand-fab-empty-circle-${index}`}
                mt={index < PER_ROW ? 0 : 2}
                px={2}
                width={1 / PER_ROW}
              >
                <Link to={`?modal=${constants.favoritesParam}`}>
                  <CircleText text={`+${brandOverflowCount}`} />
                </Link>
              </Box>
            );
          }

          // Default brand item
          return (
            <Box key={brands[index].id} mt={index < PER_ROW ? 0 : 2} px={2} width={1 / PER_ROW}>
              {renderBrandCircle(brands[index])}
            </Box>
          );
        })}
      </Flex>
    </ContentBlock>
  );
};

BrandFavoritesDialer.fragments = FRAGMENTS;

export default BrandFavoritesDialer;

// == Styles ===============================================================
